import { toast } from "sonner";
import { PlayCircle, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, InactiveNotice } from "@/components/dash/Common";
import { AttendanceBadge } from "@/components/Badges";
import { FullLoader } from "@/components/ProtectedRoute";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, isPackageActive } from "@/lib/format";

const endOf = (c) => new Date(c.start_at).getTime() + c.duration_min * 60000;

function ClassCard({ c, onCheckin, past }) {
  const nowMs = Date.now();
  const open = nowMs >= new Date(c.start_at).getTime() - 15 * 60000 && nowMs <= endOf(c);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`class-card-${c.id}`}>
      <div className="flex gap-4">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${open ? "bg-amber-500 text-slate-900" : "bg-slate-100 text-slate-500"}`}><Video className="h-5 w-5" /></div>
        <div>
          <p className="font-semibold text-slate-900">{c.title}{open && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-red-600">● Live</span>}</p>
          <p className="text-xs text-slate-500 mt-1">{fmtDateTime(c.start_at)} · {c.duration_min} menit</p>
          <div className="mt-2"><AttendanceBadge status={c.my_attendance} /></div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {!past && c.meeting_url && <Button asChild size="sm" className="bg-slate-900" data-testid={`join-class-${c.id}`}><a href={c.meeting_url} target="_blank" rel="noreferrer">Gabung Kelas</a></Button>}
        {open && c.my_attendance !== "hadir" && <Button size="sm" variant="outline" onClick={() => onCheckin(c.id)} data-testid={`checkin-class-${c.id}`}>Presensi Hadir</Button>}
        {c.recording_url && <Button asChild size="sm" variant="outline" data-testid={`recording-class-${c.id}`}><a href={c.recording_url} target="_blank" rel="noreferrer"><PlayCircle className="h-4 w-4 mr-1" />Rekaman</a></Button>}
      </div>
    </div>
  );
}

export default function StudentLive() {
  const { user } = useAuth();
  const { data, loading, reload } = useApi("/student/classes");
  if (loading) return <FullLoader />;
  const items = data || [];
  const upcoming = items.filter((c) => endOf(c) >= Date.now());
  const past = items.filter((c) => endOf(c) < Date.now()).reverse();
  const checkin = async (id) => {
    try {
      await api.post(`/student/classes/${id}/checkin`);
      toast.success("Presensi tercatat: hadir");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="student-live-page">
      <PageHeader eyebrow="Kelas Live" title="Kelas live & rekaman" description="Gabung kelas melalui tautan Zoom/Meet. Presensi dibuka 15 menit sebelum kelas dimulai." />
      {!isPackageActive(user) ? <InactiveNotice /> : (
        <Tabs defaultValue="upcoming">
          <TabsList><TabsTrigger value="upcoming" data-testid="tab-upcoming-classes">Mendatang ({upcoming.length})</TabsTrigger><TabsTrigger value="past" data-testid="tab-past-classes">Selesai & Rekaman ({past.length})</TabsTrigger></TabsList>
          <TabsContent value="upcoming" className="space-y-3 mt-4">{upcoming.length ? upcoming.map((c) => <ClassCard key={c.id} c={c} onCheckin={checkin} />) : <EmptyState text="Belum ada kelas mendatang." />}</TabsContent>
          <TabsContent value="past" className="space-y-3 mt-4">{past.length ? past.map((c) => <ClassCard key={c.id} c={c} past onCheckin={checkin} />) : <EmptyState text="Belum ada kelas yang selesai." />}</TabsContent>
        </Tabs>
      )}
    </div>
  );
}
